import React, { useEffect, useState } from 'react';
import { Row, Col, Select, Space, Button, Divider } from 'antd';

const { Option } = Select;

const BinaryFormulaBuilder = ({ indicatorCodes = [], availableIndicators = [], uniqueValues = {}, onChange, form, initialFormula = '' }) => {
    const [aBase, setABase] = useState(null);
    const [aSex, setASex] = useState(undefined);
    const [aClass1, setAClass1] = useState(undefined);
    const [aClass2, setAClass2] = useState(undefined);

    const [bBase, setBBase] = useState(null);
    const [bSex, setBSex] = useState(undefined);
    const [bClass1, setBClass1] = useState(undefined);
    const [bClass2, setBClass2] = useState(undefined);

    const [operator, setOperator] = useState('/');

    const formatVar = (base, sex, c1, c2) => {
        if (!base) return null;
        const parts = [base, sex, c1, c2].filter(Boolean);
        return parts.join('_');
    };

    useEffect(() => {
        const va = formatVar(aBase, aSex, aClass1, aClass2);
        const vb = formatVar(bBase, bSex, bClass1, bClass2);
        if (va && vb) {
            const formula = `${va} ${operator} ${vb}`;
            onChange && onChange(formula);
            if (form && typeof form.setFieldsValue === 'function') {
                form.setFieldsValue({ formulation: formula });
            }

        } else {
            onChange && onChange('');
            if (form && typeof form.setFieldsValue === 'function') {
                form.setFieldsValue({ formulation: '' });
            }
        }
    }, [aBase, aSex, aClass1, aClass2, bBase, bSex, bClass1, bClass2, operator]);

    useEffect(() => {
        if (!initialFormula || typeof initialFormula !== 'string') return;
        const v = initialFormula.trim();
        const m = v.match(/^([A-Za-z0-9_]+)\s*([+\-*\/])\s*([A-Za-z0-9_]+)$/);
        if (!m) return;
        const left = m[1];
        const op = m[2];
        const right = m[3];
        setOperator(op);

        const parseVar = (varStr) => {
                const parts = varStr.split('_').filter(Boolean);
                const uv = {
                    sex: new Set((uniqueValues.sex || []).map(x => String(x).toLowerCase())),
                    class_one: new Set((uniqueValues.class_one || []).map(x => String(x).toLowerCase())),
                    class_two: new Set((uniqueValues.class_two || []).map(x => String(x).toLowerCase())),
                };
                let p = parts.slice();
                let c2 = null, c1 = null, s = null;

                const findAndRemove = (set, maxLen) => {
                    for (let len = Math.min(maxLen, p.length); len >= 1; len--) {
                        for (let start = p.length - len; start >= 0; start--) {
                            const candidate = p.slice(start, start + len).join('_');
                            if (set.has(String(candidate).toLowerCase())) {
                                p.splice(start, len);
                                return candidate;
                            }
                        }
                    }
                    return null;
                };

                s = findAndRemove(uv.sex, 2);
                c1 = findAndRemove(uv.class_one, 3);
                c2 = findAndRemove(uv.class_two, 3);

                const base = p.join('_') || null;
                return { base, s, c1, c2 };
            };

        const leftParsed = parseVar(left);
        const rightParsed = parseVar(right);
        setABase(leftParsed.base);
        setASex(leftParsed.s);
        setAClass1(leftParsed.c1);
        setAClass2(leftParsed.c2);

        setBBase(rightParsed.base);
        setBSex(rightParsed.s);
        setBClass1(rightParsed.c1);
        setBClass2(rightParsed.c2);
    }, [initialFormula, uniqueValues]);

    useEffect(() => {
        const sexSet = new Set((uniqueValues.sex || []).map(x => String(x).toLowerCase()));

        const sanitizeIfSex = (val, setFn) => {
            if (!val) return;
            if (sexSet.has(String(val).toLowerCase())) setFn(undefined);
        };

        sanitizeIfSex(aClass1, setAClass1);
        sanitizeIfSex(aClass2, setAClass2);
        sanitizeIfSex(bClass1, setBClass1);
        sanitizeIfSex(bClass2, setBClass2);
    }, [uniqueValues.sex]);

    

    const indicatorOptions = (indicatorCodes && indicatorCodes.length > 0)
        ? indicatorCodes.map(i => ({ value: i.indicator || i.indicator, label: i.label }))
        : availableIndicators.map(i => ({ value: i.ind_code, label: i.ind_name_eng }));

    const renderIndicatorSelect = (value, onChangeFn, placeholder = 'Select ILO/indicator code') => (
        <Select
            showSearch
            autoClearSearchValue={false}
            placeholder={placeholder}
            value={value}
            onChange={onChangeFn}
            style={{ width: '100%' }}
            optionFilterProp="children"
        >
            {indicatorOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>
                    {opt.value}
                </Option>
            ))}
        </Select>
    );
             
    const renderClassSelect = (value, onChangeFn, list = [], placeholder = '') => (
        <Select
            showSearch
            autoClearSearchValue={false}
            placeholder={placeholder}
            value={value}
            onChange={onChangeFn}
            allowClear
            style={{ width: '100%' }}
        >
            {(list || []).map(v => (
                <Option key={v} value={v}>{v}</Option>
            ))}
        </Select>
    );

    const sexSet = new Set((uniqueValues.sex || []).map(x => String(x).toLowerCase()));
    const classOneOptions = (uniqueValues.class_one || []).filter(v => !sexSet.has(String(v).toLowerCase()));
    const classTwoOptions = (uniqueValues.class_two || []).filter(v => !sexSet.has(String(v).toLowerCase()));

    return (
        <div style={{ padding: 8, border: '1px dashed #e6f7ff', borderRadius: 4, marginBottom: 12 }}>
            <Row gutter={8} style={{ marginBottom: 8 }}>
                <Col span={24}><strong>Formula Builder</strong></Col>
            </Row>
            <Row gutter={8}>
                <Col span={24}>
                    <div style={{ fontSize: 12 }}>Variable A</div>
                    <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
                        {renderIndicatorSelect(aBase, setABase, 'Select base ILO code')}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 33%', minWidth: 0 }}>{renderClassSelect(aSex, setASex, uniqueValues.sex || [], 'Sex')}</div>
                            <div style={{ flex: '1 1 33%', minWidth: 0 }}>{renderClassSelect(aClass1, setAClass1, classOneOptions, 'Class 1')}</div>
                            <div style={{ flex: '1 1 33%', minWidth: 0 }}>{renderClassSelect(aClass2, setAClass2, classTwoOptions, 'Class 2')}</div>
                        </div>
                    </Space>
                </Col>

                <Col span={24} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Select value={operator} onChange={setOperator} style={{ width: 80 }}>
                            <Option value="+">+</Option>
                            <Option value="-">-</Option>
                            <Option value="*">*</Option>
                            <Option value="/">/</Option>
                        </Select>
                    </div>
                </Col>

                <Col span={24}>
                    <div style={{ fontSize: 12}}>Variable B</div>
                    <Space direction="vertical" style={{ width: '100%'  }}>
                        {renderIndicatorSelect(bBase, setBBase, 'Select base ILO code')}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 33%', minWidth: 0 }}>{renderClassSelect(bSex, setBSex, uniqueValues.sex || [], 'Sex')}</div>
                            <div style={{ flex: '1 1 33%', minWidth: 0 }}>{renderClassSelect(bClass1, setBClass1, classOneOptions, 'Class 1')}</div>
                            <div style={{ flex: '1 1 33%', minWidth: 0 }}>{renderClassSelect(bClass2, setBClass2, classTwoOptions, 'Class 2')}</div>
                        </div>
                        
                    </Space>
                </Col>
            </Row>
        </div>
    );
};

export default BinaryFormulaBuilder;
